import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type SupabaseOAuth = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};

function oauthClient(): SupabaseOAuth {
  return (supabase.auth as unknown as { oauth: SupabaseOAuth }).oauth;
}

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    const next = location.pathname + location.searchStr;
    if (!data.session) throw redirect({ to: "/login", search: { next } });
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await oauthClient().getAuthorizationDetails(authorizationId);
    if (error) throw error;
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="container-edge py-20">
      Kunde inte läsa denna auktoriseringsförfrågan: {String((error as Error)?.message ?? error)}
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData() as any;
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const { data, error } = approve
      ? await oauthClient().approveAuthorization(authorization_id)
      : await oauthClient().denyAuthorization(authorization_id);
    if (error) {
      setBusy(false);
      setError(error.message ?? "Något gick fel");
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("Ingen omdirigering returnerades.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "en app";

  return (
    <section className="bg-warm">
      <div className="container-edge flex min-h-[80vh] max-w-md flex-col justify-center py-20">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10 elev-shadow">
          <p className="eyebrow">Auktorisera</p>
          <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-4xl">
            Anslut <span className="font-italic-display text-accent">{clientName}</span> till ditt konto
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Detta ger {clientName} åtkomst till AK Husbil & Fritid som dig via MCP: din profil,
            dina reservdelsförfrågningar och dina campingtips.
          </p>
          {error && (
            <p role="alert" className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="mt-8 flex gap-3">
            <button
              disabled={busy}
              onClick={() => decide(true)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 py-3.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
            >
              Godkänn
            </button>
            <button
              disabled={busy}
              onClick={() => decide(false)}
              className="inline-flex flex-1 items-center justify-center rounded-full border border-border bg-background px-5 py-3.5 text-sm font-medium transition hover:bg-secondary disabled:opacity-60"
            >
              Neka
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
